// src/pages/Accounting/Reconcile/storage.ts
//
// LocalStorage helpers for Reconcile drafts.
// Keyed per account under `novala:reconcile:draft:<accountId>`.
// Later swapped for a backend API without touching UI components.

import type { ReconciliationDraft } from "./types";

const KEY_PREFIX = "novala:reconcile:draft:";

export function saveDraft(draft: ReconciliationDraft): void {
  try {
    localStorage.setItem(KEY_PREFIX + draft.accountId, JSON.stringify(draft));
  } catch {
    // Private browsing or storage disabled; fail silently
  }
}

export function loadDraft(accountId: string): ReconciliationDraft | null {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + accountId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ReconciliationDraft;
    if (!parsed.accountId || !parsed.savedAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearDraft(accountId: string): void {
  try {
    localStorage.removeItem(KEY_PREFIX + accountId);
  } catch {
    // ignore
  }
}

/**
 * Return the most recently saved draft across any account.
 * Used on page mount to surface the resume banner.
 */
export function loadAnyDraft(): ReconciliationDraft | null {
  try {
    let latest: ReconciliationDraft | null = null;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(KEY_PREFIX)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw) as ReconciliationDraft;
        if (!parsed.accountId || !parsed.savedAt) continue;
        if (!latest || parsed.savedAt > latest.savedAt) {
          latest = parsed;
        }
      } catch {
        continue;
      }
    }
    return latest;
  } catch {
    return null;
  }
}